// components/SeasonalBackground.js
import React, { useMemo } from 'react';
import { useSeasonalTheme } from '../context/SeasonalThemeContext';
import HalloweenExtras from './HalloweenExtras';
import './SeasonalBackground.css';

const SUNFLOWER_COUNT = 14;
const YELLOW_LIGHT_COUNT = 22;
const HALLOWEEN_DAY_COUNT = 16;
const HALLOWEEN_DAY_EMOJIS = ['🎃', '🎃', '🍂', '🍁'];
const BAT_COUNT = 3;
const EMBER_COUNT = 18;

// Shared with Login.js and Layout.js — whichever full-viewport,
// non-scrolling, position:relative box it's mounted inside of (both
// `.login-container` and `.layout-root` already qualify) gets the
// sunflower/yellow-lights particle overlay for the currently active
// seasonal theme, via `position: absolute; inset: 0`.
//
// Which one shows in which mode is CSS-driven ([data-theme="dark"] rules in
// SeasonalBackground.css), not JS state, so it reacts instantly to
// NightModeToggle without this component needing to track the toggle itself.
export default function SeasonalBackground() {
  const { activeTheme } = useSeasonalTheme();
  const hasSunflowers = activeTheme?.login_bg_light === 'sunflowers';
  const hasYellowLights = activeTheme?.login_bg_dark === 'yellow_lights';
  const hasHalloweenDay = activeTheme?.login_bg_light === 'halloween_day';
  const hasHalloweenNight = activeTheme?.login_bg_dark === 'halloween_night';

  // Randomized once per mount (not per render) — negative delays make the
  // very first frame already look mid-animation instead of every particle
  // popping in from the same starting point at once.
  const sunflowers = useMemo(() => {
    if (!hasSunflowers) return [];
    return Array.from({ length: SUNFLOWER_COUNT }, () => {
      const duration = 12 + Math.random() * 10;
      return {
        left: Math.random() * 100,
        size: 18 + Math.random() * 16,
        sway: 20 + Math.random() * 50,
        duration,
        delay: -Math.random() * duration,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSunflowers]);

  const yellowLights = useMemo(() => {
    if (!hasYellowLights) return [];
    return Array.from({ length: YELLOW_LIGHT_COUNT }, () => {
      const fallDuration = 8 + Math.random() * 10;
      const twinkleDuration = 2 + Math.random() * 3;
      return {
        left: Math.random() * 100,
        size: 3 + Math.random() * 6,
        fallDuration,
        fallDelay: -Math.random() * fallDuration,
        twinkleDuration,
        twinkleDelay: -Math.random() * twinkleDuration,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasYellowLights]);

  // 🎃 Halloween (light): pumpkins + autumn leaves, reusing the sunflower
  // fall/sway animation with a mixed emoji set.
  const halloweenDay = useMemo(() => {
    if (!hasHalloweenDay) return [];
    return Array.from({ length: HALLOWEEN_DAY_COUNT }, () => {
      const duration = 11 + Math.random() * 10;
      return {
        emoji: HALLOWEEN_DAY_EMOJIS[Math.floor(Math.random() * HALLOWEEN_DAY_EMOJIS.length)],
        left: Math.random() * 100,
        size: 18 + Math.random() * 16,
        sway: 20 + Math.random() * 60,
        swayDuration: 2 + Math.random() * 2,
        duration,
        delay: -Math.random() * duration,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHalloweenDay]);

  // 🦇 Halloween (dark): bats crossing the screen at different heights and
  // speeds, plus orange embers drifting up from the bottom.
  const halloweenNight = useMemo(() => {
    if (!hasHalloweenNight) return { bats: [], embers: [] };
    const bats = Array.from({ length: BAT_COUNT }, () => {
      const duration = 14 + Math.random() * 14;
      return {
        top: 5 + Math.random() * 55,
        size: 26 + Math.random() * 22,
        duration,
        delay: -Math.random() * duration,
        flap: 0.25 + Math.random() * 0.2,
        bob: 15 + Math.random() * 35,
        bobDuration: 2.5 + Math.random() * 2.5,
        reverse: Math.random() < 0.4,
      };
    });
    const embers = Array.from({ length: EMBER_COUNT }, () => {
      const riseDuration = 9 + Math.random() * 10;
      const twinkleDuration = 1.5 + Math.random() * 2.5;
      return {
        left: Math.random() * 100,
        size: 2 + Math.random() * 5,
        riseDuration,
        riseDelay: -Math.random() * riseDuration,
        twinkleDuration,
        twinkleDelay: -Math.random() * twinkleDuration,
      };
    });
    return { bats, embers };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHalloweenNight]);

  if (!hasSunflowers && !hasYellowLights && !hasHalloweenDay && !hasHalloweenNight) return null;

  return (
    <>
      {/* Webs + dropping spider, moon + fog (dark), peeking ghost */}
      {(hasHalloweenDay || hasHalloweenNight) && <HalloweenExtras />}
      {hasSunflowers && (
        <div className="seasonal-sunflowers" aria-hidden="true">
          {sunflowers.map((s, i) => (
            <span
              key={i}
              className="seasonal-sunflower"
              style={{
                left: `${s.left}%`,
                fontSize: s.size,
                animationDuration: `${s.duration}s, ${2 + Math.random() * 2}s`,
                animationDelay: `${s.delay}s, ${s.delay}s`,
                '--sway': `${s.sway}px`,
              }}
            >
              🌻
            </span>
          ))}
        </div>
      )}
      {hasYellowLights && (
        <div className="seasonal-yellow-lights" aria-hidden="true">
          {yellowLights.map((l, i) => (
            <span
              key={i}
              className="seasonal-yellow-light"
              style={{
                left: `${l.left}%`,
                width: l.size,
                height: l.size,
                animationDuration: `${l.fallDuration}s, ${l.twinkleDuration}s`,
                animationDelay: `${l.fallDelay}s, ${l.twinkleDelay}s`,
              }}
            />
          ))}
        </div>
      )}
      {hasHalloweenDay && (
        <div className="seasonal-halloween-day" aria-hidden="true">
          {halloweenDay.map((p, i) => (
            <span
              key={i}
              className="seasonal-sunflower"
              style={{
                left: `${p.left}%`,
                fontSize: p.size,
                animationDuration: `${p.duration}s, ${p.swayDuration}s`,
                animationDelay: `${p.delay}s, ${p.delay}s`,
                '--sway': `${p.sway}px`,
              }}
            >
              {p.emoji}
            </span>
          ))}
        </div>
      )}
      {hasHalloweenNight && (
        <div className="seasonal-halloween-night" aria-hidden="true">
          {halloweenNight.embers.map((e, i) => (
            <span
              key={`e${i}`}
              className="seasonal-ember"
              style={{
                left: `${e.left}%`,
                width: e.size,
                height: e.size,
                animationDuration: `${e.riseDuration}s, ${e.twinkleDuration}s`,
                animationDelay: `${e.riseDelay}s, ${e.twinkleDelay}s`,
              }}
            />
          ))}
          {halloweenNight.bats.map((b, i) => (
            // Outer span flies across + bobs; inner span flaps, so the two
            // transforms don't overwrite each other.
            <span
              key={`b${i}`}
              className={`seasonal-bat${b.reverse ? ' seasonal-bat--reverse' : ''}`}
              style={{
                top: `${b.top}%`,
                fontSize: b.size,
                animationDuration: `${b.duration}s, ${b.bobDuration}s`,
                animationDelay: `${b.delay}s, ${b.delay}s`,
                '--bob': `${b.bob}px`,
              }}
            >
              <span className="seasonal-bat-wings" style={{ animationDuration: `${b.flap}s` }}>🦇</span>
            </span>
          ))}
        </div>
      )}
    </>
  );
}
